import { Injectable, Inject } from '@angular/core';
import { FilterGridColDef } from '../covance-filter';
import { LabTestResult, LabTestSortArgs } from '../lab-test-results';
import { isEmpty, isNil, sortBy, chain, includes, find, max, groupBy, keys, uniq } from 'lodash';
import { LabTestResultGridRowItem } from '../on-screen-results';
import { ColDef } from 'ag-grid-community';
import { dateTimeRenderer } from '../covance-ag-grid';
import { VisitColumn, TestCellValues, TestResultIds, ResultData, VisitNumberData, VisitDisplayNameData, VisitResult } from './subject-pivot-interfaces';
import { LabTestGridVisitComponent } from './lab-tests-grid-visit.component';
import { LabTestGridVisitHeaderComponent } from './lab-tests-grid-visit-header.component';
import { LabTestsGridColumns } from './lab-tests-grid-columns';
import { LabTestResultsLinkBuilder, LAB_TEST_RESULTS_LINK_BUILDER } from './lab-test-results-link-builder';

interface BuilderResult {
    columns: FilterGridColDef[];
    exportColumns: ColDef[];
}

@Injectable()
export class LabTestsGridColumnsBuilder {
    constructor(
        private labTestsGridColumns: LabTestsGridColumns,
        @Inject(LAB_TEST_RESULTS_LINK_BUILDER) private labTestResultsLinkBuilder: LabTestResultsLinkBuilder) { }

    public getColumns(res: LabTestResult[]): BuilderResult {
        const baseColumns = this.labTestsGridColumns.getBaseColumns(res);

        if (isEmpty(res)) {
            return {
                columns: baseColumns,
                exportColumns: null
            };
        }
        const sortTestArgs: LabTestSortArgs = ['testId', 'testGroup', 'visitColumnKey'];
        const sortVisitArgs: LabTestSortArgs = ['visitColumnKey', 'testId', 'testGroup'];
        const exportColumns = this.labTestsGridColumns.getColumnsForExport(baseColumns);
        const sortedLabsByTestAndVisit = sortBy(res, sortTestArgs);
        const uniqueTestIds = chain(sortedLabsByTestAndVisit).uniqBy(x => x.uniqLabTestIdKey).map(t => t.uniqLabTestIdKey).value();
        const sortedLabsByVisitAndTest = sortBy(res, sortVisitArgs);
        const newCol: VisitColumn[] = [];
        const testCellValues: TestCellValues = {};
        const testResultIds: TestResultIds = {};
        let theVisitResults: ResultData[] = [];
        let theResultIds: number[] = [];
        let visitCellResult: { [visitId: string]: ResultData[] } = {};
        let visitResultIds: VisitNumberData = {};
        let theTestId = sortedLabsByTestAndVisit[0].testId;
        let theTestGroup = sortedLabsByTestAndVisit[0].testGroup;
        let theVisitId = sortedLabsByTestAndVisit[0].visitColumnKey;
        const visitDisplayNameData: VisitDisplayNameData[] = [];

        sortedLabsByTestAndVisit.forEach((lab: LabTestResult) => {
            if (theTestId !== lab.testId || theTestGroup !== lab.testGroup) {
                theVisitResults = [];
                visitCellResult = {};
                visitResultIds = {};
            }

            if (theVisitId !== lab.visitColumnKey) {
                theVisitResults = [];
                theResultIds = [];
            }

            visitDisplayNameData.push({ visitId: lab.visitColumnKey, testId: lab.testId, testGroup: lab.testGroup, visitDescription: lab.visitDescription, visitTitle: lab.visitTitle });

            const testResultId = lab.labTestId;
            const resultData: ResultData = {
                result: '',
                flags: lab.flags,
                comments: lab.comments,
                cancellationReason: lab.cancellationReason,
                isCancelled: lab.isCancelled,
                isFlagged: lab.isFlagged,
                hasDataRevision: lab.hasResultDataRevision,
                labTestId: lab.labTestId,
                accessionNumber: lab.accessionNumber,
                visitDescription: lab.visitDescription
            };

            const tempResult = lab.currentResult;
            if (!isNil(tempResult)) {
                const theResult = tempResult.stringResult;

                if (theResult) {
                    resultData.result = theResult;
                    resultData.sequence = tempResult.sequence;
                }
            }


            theVisitResults[testResultId] = resultData;
            visitCellResult[lab.visitColumnKey] = theVisitResults;

            if (isEmpty(theResultIds) || !includes(theResultIds, testResultId)) {
                theResultIds.push(testResultId);
            }

            visitResultIds[lab.visitColumnKey] = theResultIds;
            theTestId = lab.testId;
            theTestGroup = lab.testGroup;
            theVisitId = lab.visitColumnKey;

            testCellValues[lab.uniqLabTestIdKey] = visitCellResult;
            testResultIds[lab.uniqLabTestIdKey] = visitResultIds;
        });

        this.gatherVisitColumnInfo(
            res,
            sortedLabsByVisitAndTest,
            uniqueTestIds,
            testCellValues,
            testResultIds,
            visitDisplayNameData,
            newCol);

        const visitColumns = this.getVisitColumns(newCol, this.getLineCountDict(res));

        return {
            columns: [...baseColumns, ...visitColumns],
            exportColumns
        };
    }

    private getLineCountDict(res: LabTestResult[]): { [uniqueTestId: string]: number } {
        const maxLineCounts = chain(res)
            .groupBy((r: LabTestResult) => r.uniqLabTestIdKey)
            .mapValues((valsByUniqueTestId: LabTestResult[]) => {

                const counts = chain(valsByUniqueTestId)
                    .groupBy((r: LabTestResult) => r.visitColumnKey)
                    .map((valsByAccessionId: LabTestResult[]) => valsByAccessionId.length)
                    .value();

                return max(counts);
            })
            .value();

        return maxLineCounts;
    }

    private getVisitColumns(col: VisitColumn[], maxLineCounts: { [uniqueTestId: string]: number }): VisitResult[] {
        const visitColumns = chain(col)
            .map((vc: VisitColumn): VisitResult => {
                return {
                    orderByKey: vc.orderByKey,
                    visitColumnKey: vc.visitColumnKey,
                    getLineCount: (entity: LabTestResult) => maxLineCounts[entity.uniqLabTestIdKey] || 1,
                    getRowHeight: (entity: LabTestResult) => {
                        //the value of lineHeight should match the one in grid.scss
                        const lineHeight = 23;
                        const maxCount = maxLineCounts[entity.uniqLabTestIdKey] || 1;
                        return maxCount * lineHeight;
                    },
                    headerName: vc.headerName,
                    cellRendererParams: {
                        visitId: vc.visitId,
                        testResultIds: vc.testResultIds,
                        cellValues: vc.cellValues
                    },
                    cellRendererFramework: LabTestGridVisitComponent,
                    headerComponentFramework: LabTestGridVisitHeaderComponent,
                    headerComponentParams: vc.headerComponentParams,
                    getQuickFilterText: (params: LabTestResultGridRowItem) => this.getPlainTextResultsAndFlags(params)
                };
            })
            .sortBy(x => x.orderByKey)
            .value();

        return visitColumns;
    }

    private getPlainTextResultsAndFlags(params: LabTestResultGridRowItem): string {
        if (!params || !params.data) {
            return '';
        }

        const testId = params.data.uniqLabTestIdKey;
        const cellParams = (params.colDef as VisitResult).cellRendererParams;
        const visitId = cellParams.visitId;
        const theCellValues = cellParams.cellValues;
        const testResultIds = cellParams.testResultIds;

        if (!theCellValues || !theCellValues[testId] || !theCellValues[testId][visitId]) {
            return '';
        }

        const cellValues = theCellValues[testId][visitId];

        if (!testResultIds[testId] || !testResultIds[testId][visitId]) {
            return '';
        }

        let searchText = '';

        testResultIds[testId][visitId].forEach(item => {

            const data = cellValues[item];

            if (data) {
                const commentExists = !isEmpty(data.comments);
                const cellFlags: string[] = [];

                if (data.isCancelled) {
                    searchText += ` ${data.cancellationReason} `;
                } else if (data.isFlagged) {
                    const flagGroups = groupBy(data.flags, f => f.category);

                    keys(flagGroups).sort().forEach((category: string) => {
                        const flagNames = flagGroups[category].map(f => f.name);
                        const flags = uniq(flagNames).sort().join(' ');
                        cellFlags.push(flags);
                    });
                }

                searchText += ` ${data.result} ${cellFlags.join(', ')} `;
            }
        });

        return searchText;
    }

    private gatherVisitColumnInfo(
        res: LabTestResult[], sortedLabsByVisitAndTest: LabTestResult[], uniqueTestIds: string[],
        testCellValues: TestCellValues,
        testResultIds: TestResultIds,
        visitDisplayNameData: VisitDisplayNameData[], newCol: VisitColumn[]
    ) {
        sortedLabsByVisitAndTest.forEach((lab: LabTestResult) => {
            let theVisitDisplayName = '';
            const existingVisit = find(newCol, (x: VisitColumn) => lab.visitColumnKey === x.visitId);

            if (existingVisit) {
                return;
            }

            theVisitDisplayName = this.getVisitDisplayName(visitDisplayNameData.filter(t => t.visitId === lab.visitColumnKey));

            const accessionNumberLinkData = this.labTestResultsLinkBuilder.getAccessionNumberLink(lab);
            const cellValues: TestCellValues = {};
            const testResId: { [testId: string]: { [visitId: number]: number[] } } = {};

            uniqueTestIds.forEach((testId: string) => {
                cellValues[testId] = testCellValues[testId];
                testResId[testId] = testResultIds[testId];
            });

            const visitColumn: VisitColumn = {
                visitColumnKey: lab.visitColumnKey,
                orderByKey: lab.visitCollectionDateValue,
                headerName: theVisitDisplayName,
                visitId: lab.visitColumnKey,
                testResultIds: testResId,
                cellValues: cellValues,
                headerComponentParams: {
                    displayName: theVisitDisplayName,
                    visitDate: dateTimeRenderer({ value: lab.visitCollectionDate }) || '[missing date]',
                    hasDataRevision: res.some(r => r.visitColumnKey === lab.visitColumnKey && r.hasVisitDataRevision),
                    routerLink: accessionNumberLinkData.routerLink,
                    queryParams: accessionNumberLinkData.queryParams,
                    text: accessionNumberLinkData.text,
                    labTestId: res.find(r => r.visitColumnKey === lab.visitColumnKey && r.hasVisitDataRevision)?.labTestId,
                    subjectNumber: res.find(r => r.visitColumnKey === lab.visitColumnKey && r.hasVisitDataRevision)?.subjectNumber
                }
            };

            newCol.push(visitColumn);
        });
    }

    private getVisitDisplayName(visitDisplayNameData: VisitDisplayNameData[]): string {
        let visitDisplayName = '';

        if (isEmpty(visitDisplayNameData)) {
            return '';
        }

        const sortInfo: LabTestSortArgs = ['testId', 'testGroup'];
        const sortedVisitDisplayNameData = sortBy(visitDisplayNameData, sortInfo);

        sortedVisitDisplayNameData.forEach((item: VisitDisplayNameData) => {
            if (isEmpty(visitDisplayName)) {
                if (!item.testId) {
                    return;
                }

                if (item.visitDescription !== null) {
                    visitDisplayName = item.visitDescription;
                } else {
                    if (item.visitTitle !== null) {
                        visitDisplayName = item.visitTitle;
                    }
                }
            }
        });

        return visitDisplayName;
    }
}